import { useSearchParams } from 'react-router';
import type { ListQuery } from './api';
import { parsePageSize, useListParams } from '@/components/list/use-list-params';

export const PURPOSES_PATH = '/expenses/settings/purposes';
function parsePurposeQuery(params: URLSearchParams) {
  const view = params.get('view') ?? 'active';
  const defaultActive: Record<string, string> = { active: 'true', archived: 'false' };
  const active = params.get('active') ?? defaultActive[view] ?? null;
  const restricted = params.get('restricted') ?? (view === 'reserved' ? 'true' : null);
  const noteType = params.get('noteType');
  const sort =
    (['code', 'label', 'active', 'restricted', 'updatedAt', 'noteTypes'] as const).find(
      (v) => v === params.get('sort'),
    ) ?? 'code';
  const query: ListQuery = {
    search: params.get('q') ?? '',
    page: Math.max(1, Math.min(100000, Math.floor(Number(params.get('page')) || 1))),
    pageSize: parsePageSize(params.get('pageSize')),
    sort,
    direction: params.get('direction') === 'desc' ? 'desc' : 'asc',
    ...(isFlag(active) ? { active } : {}),
    ...(isFlag(restricted) ? { restricted } : {}),
    ...(noteType === 'travel' || noteType === 'reimbursement' ? { noteType } : {}),
  };

  return { view, active, restricted, query };
}
export function usePurposeQuery() {
  const list = useListParams(['active', 'restricted', 'noteType']);
  const [, setParams] = useSearchParams();
  const params = list.params;
  const parsed = parsePurposeQuery(params);
  const { active, restricted } = parsed;
  const query = { ...parsed.query, search: list.q, pageSize: list.pageSize };
  function change(key: string, value: string | null) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
        if (key !== 'page') next.delete('page');
        if (key === 'active' || key === 'restricted') {
          next.set('view', 'all');
          if (key === 'active' && restricted) next.set('restricted', restricted);
          if (key === 'restricted' && active) next.set('active', active);
        }
        return next;
      },
      { replace: true },
    );
  }
  function selectView(value: string) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const key of ['active', 'restricted', 'page']) next.delete(key);
        next.set('view', value);
        return next;
      },
      { replace: true },
    );
  }
  function clear() {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const key of ['q', 'active', 'restricted', 'noteType', 'page']) next.delete(key);
        next.set('view', 'all');
        return next;
      },
      { replace: true },
    );
  }
  function target(id: string) {
    const next = new URLSearchParams(params);
    next.set('purpose', id);
    return `${PURPOSES_PATH}?${next}`;
  }
  function order(column: string) {
    sortBy(`${column}:${query.sort === column && query.direction === 'asc' ? 'desc' : 'asc'}`);
  }
  function close() {
    list.set('purpose', null);
  }
  function sortBy(value: string) {
    const [column, direction] = value.split(':');
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('sort', column!);
        next.set('direction', direction!);
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  const actions = { change, selectView, clear, target, close, order, sortBy };
  return { params, ...parsed, query, ...actions };
}

function isFlag(value: string | null): value is 'true' | 'false' {
  return value === 'true' || value === 'false';
}
